import { CurrencyAmount, Rounding } from '../sdk'
import { formatAmountInputDisplay } from './amountInputFormat'

/** Decimales visibles según magnitud del monto y precisión del token. */
export function resolveSwapAmountDisplayDecimals(raw: string, tokenDecimals = 18): number {
  const normalized = raw.replace(/,/g, '')
  if (normalized === '' || normalized === '0') return 2

  const num = Number(normalized)
  if (!Number.isFinite(num) || num === 0) return 2

  const abs = Math.abs(num)
  const cap = Math.max(0, Math.min(tokenDecimals, 18))

  if (abs >= 1_000) return Math.min(2, cap)
  if (abs >= 1) return Math.min(6, cap)
  if (abs >= 0.01) return Math.min(6, cap)
  if (abs >= 0.000_001) return Math.min(8, cap)
  return Math.min(12, cap)
}

/** Límite de decimales al editar: conserva precisión mientras se escribe 0.000… */
export function resolveSwapAmountInputDecimals(raw: string, tokenDecimals = 18): number {
  const normalized = raw.replace(/,/g, '')
  if (normalized === '' || normalized.endsWith('.')) {
    return Math.min(tokenDecimals, 18)
  }

  const num = Number(normalized)
  if (!Number.isFinite(num) || num === 0) {
    return Math.min(tokenDecimals, 18)
  }

  return resolveSwapAmountDisplayDecimals(raw, tokenDecimals)
}

/** Trunca hacia abajo el string crudo al límite adaptivo de decimales. */
export function floorSwapAmountRaw(raw: string, tokenDecimals = 18): string {
  if (!raw) return ''

  const maxFractionDigits = resolveSwapAmountInputDecimals(raw, tokenDecimals)
  const dotIndex = raw.indexOf('.')
  if (dotIndex < 0) return raw

  const integerPart = raw.slice(0, dotIndex) || '0'
  let fractionalPart = raw.slice(dotIndex + 1)

  if (fractionalPart.length > maxFractionDigits) {
    fractionalPart = fractionalPart.slice(0, maxFractionDigits)
  }

  const isZeroTyped = Number(`${integerPart}.${fractionalPart || '0'}`) === 0 && fractionalPart.length > 0
  if (!isZeroTyped) {
    fractionalPart = fractionalPart.replace(/0+$/, '')
  }
  if (fractionalPart === '') return integerPart
  return `${integerPart}.${fractionalPart}`
}

/** Trunca hacia abajo un CurrencyAmount con decimales adaptivos (seguro para MAX/presets). */
export function toFlooredSwapAmount(amount: CurrencyAmount): string {
  const maxFractionDigits = resolveSwapAmountDisplayDecimals(
    amount.toExact(),
    amount.currency.decimals,
  )
  return amount.toFixed(maxFractionDigits, {}, Rounding.ROUND_DOWN)
}

/** Normaliza entrada del usuario: conserva punto final mientras escribe. */
export function normalizeSwapAmountInput(raw: string, tokenDecimals = 18): string {
  if (!raw) return ''
  if (raw.endsWith('.')) return raw
  return floorSwapAmountRaw(raw, tokenDecimals)
}

/** Separadores de miles para montos ya normalizados en estado. */
export function formatSwapAmountDisplay(raw: string): string {
  return formatAmountInputDisplay(raw)
}
